/**
 * Utility functions for the application
 */
import { TIMEZONE } from './constants';

/**
 * Format a phone number as ###-###-####
 * @param phoneNumber The phone number to format
 * @returns The formatted phone number
 */
export const formatPhoneNumber = (phoneNumber: string): string => {
  // Remove all non-digit characters
  const cleaned = phoneNumber.replace(/\D/g, '');

  // Format as ###-###-####
  if (cleaned.length === 10) {
    return cleaned.replace(/(\d{3})(\d{3})(\d{4})/, '$1-$2-$3');
  }

  // Return original if not 10 digits
  return phoneNumber;
};

/**
 * Format a date string to mm/dd/yyyy format
 * @param dateString The date string to format
 * @returns The formatted date string in mm/dd/yyyy format
 */
export const formatDate = (dateString: string): string => {
  if (!dateString) return '';

  // Parse the date parts directly from the string to avoid timezone issues
  if (dateString.match(/^\d{4}-\d{2}-\d{2}$/)) {
    // Format: YYYY-MM-DD
    const [year, month, day] = dateString.split('-');
    return `${month}/${day}/${year}`;
  }

  // For other formats, use the Date object but be careful with timezone
  const date = new Date(dateString);
  if (isNaN(date.getTime())) return dateString; // Return original if invalid date

  // Use UTC methods to avoid timezone shifts
  const month = (date.getUTCMonth() + 1).toString().padStart(2, '0');
  const day = date.getUTCDate().toString().padStart(2, '0');
  const year = date.getUTCFullYear();

  return `${month}/${day}/${year}`;
};

/**
 * Generate a timestamp in ISO format using Eastern (NY) timezone
 * @returns ISO formatted timestamp string in Eastern timezone
 */
export const generateEasternTimestamp = (): string => {
  const now = new Date();

  // Format the date in Eastern timezone
  const easternTime = new Intl.DateTimeFormat('en-US', {
    timeZone: TIMEZONE,
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit',
    hour12: false
  }).format(now);

  // Parse the formatted date back to a Date object
  // Format: MM/DD/YYYY, HH:MM:SS
  const [datePart, timePart] = easternTime.split(', ');
  const [month, day, year] = datePart.split('/');
  const [hour, minute, second] = timePart.split(':');

  // Create a UTC date string with the Eastern time components
  // Format: YYYY-MM-DDTHH:MM:SS.sssZ
  // This keeps the time as specified in Eastern timezone but marks it as UTC
  // so it won't get shifted again when used in the database
  const isoString = `${year}-${month.padStart(2, '0')}-${day.padStart(2, '0')}T${hour.padStart(2, '0')}:${minute.padStart(2, '0')}:${second.padStart(2, '0')}.000Z`;

  return isoString;
};
